// ft3d-hud/index.ts — boot for the FT3D HUD surfaces (three-state log + standalone dice window).
//
// Ships in the pre-template aggregate, which is enqueued on every page, so the whole module is a
// no-op unless the route opted in by rendering includes/sidepanels/ft3d_hud.php (which emits the
// [data-ft3d-hud] root). That is what keeps the ~40 non-FT3D openSideLog() callers untouched.
//
// Replaces the owner-rejected roll dock: no control here reopens #sideLog.

import { mountFt3dLog } from './log';
import { ft3dActorShortcuts } from '../shared/ft3d-actor-shortcuts';
import { renderFt3dControlGuidance } from './control-guidance';
// Parse-time bridge: the campaign's inline DOM-ready shell uses this shared policy.
Object.assign(window, { ft3dActorShortcuts, ftRenderFt3dControlGuidance: renderFt3dControlGuidance });
import { mountFt3dChat } from './chat';
import { mountFt3dFeedTabs } from './feed-tabs';
import { mountFt3dDiceWindow } from './dice-window';
import { mountFt3dFormsWindow } from './forms-window';
import { mountFt3dWarningMirror } from './warning-mirror';
import { mountFt3dHudPrefs } from './hud-prefs';
import { makeFt3dWindowDraggable, restoreFt3dWindowPosition } from './draggable';
import { mountFt3dDamageFeedback } from './damage-feedback';
import { mountFt3dMagicVfxFeedback } from './magic-vfx-feedback';
import { mountAreaVfxWarm } from './area-vfx-warm';
import { mountFt3dActiveWindows } from './active-window';
import { mountFt3dFeedLayer } from './feed-layer';

function boot(): void {
    const root = document.querySelector<HTMLElement>('[data-ft3d-hud]');
    if (!root) return;
    // Set FIRST: side-panels-init.ts reads this class to suppress the legacy drawer, and a feature
    // that opens it during its own boot must already be gated by the time it runs.
    document.documentElement.classList.add('ft3d-hud-active');
    // Mount before Dice/Forms apply their persisted visibility. A restored open window should become
    // active just like one opened by a click, while the campaign's two initially-visible panels stay
    // neutral until the viewer chooses one.
    mountFt3dActiveWindows();
    mountFt3dLog(root);
    // After the log: chat appends into the stream the log just bound, and the tabs read both.
    mountFt3dChat(root);
    mountFt3dFeedTabs(root);
    mountFt3dDiceWindow(root);
    mountFt3dFormsWindow(root);
    mountFt3dWarningMirror(root);
    mountFt3dDamageFeedback();
    mountFt3dMagicVfxFeedback();
    mountAreaVfxWarm();
    // The Settings window's Log tab, when the route has one. Must run AFTER the two surfaces so it
    // syncs from their applied state rather than from storage a second time.
    mountFt3dHudPrefs();
    // Bind log/chat/tabs first, then move the same live feed out of the high HUD context.
    mountFt3dFeedLayer(root);

    // The HUD's own windows drag by their bar and remember where they were left. The frame is the
    // FT3D surface when the route has one, so a window cannot be dragged off the scene.
    const frame = document.querySelector<HTMLElement>('[data-ft3d-campaign-encounter-view]');
    root.querySelectorAll<HTMLElement>('[data-ft3d-dice-window], [data-ft3d-forms-window]').forEach((win) => {
        const bar = win.querySelector<HTMLElement>('.ft3d-hud__window-bar');
        const name = win.id || win.getAttribute('aria-label') || 'window';
        if (bar) makeFt3dWindowDraggable(name, win, bar, frame);
        restoreFt3dWindowPosition(name, win, frame);
    });

    // my_campaign.php's window kit shares the implementation rather than keeping its own.
    (window as unknown as {
        ftMakeFt3dWindowDraggable?: typeof makeFt3dWindowDraggable;
        ftRestoreFt3dWindowPosition?: typeof restoreFt3dWindowPosition;
    }).ftMakeFt3dWindowDraggable = makeFt3dWindowDraggable;
    (window as unknown as {
        ftRestoreFt3dWindowPosition?: typeof restoreFt3dWindowPosition;
    }).ftRestoreFt3dWindowPosition = restoreFt3dWindowPosition;
}

// Mark the route SYNCHRONOUSLY at parse time when the marker is already in the DOM. This bundle is
// enqueued in the footer BELOW includes/sidepanels/ft3d_hud.php, so on an FT3D route it is — and
// that closes the window in which a feature booting before us could still open the legacy drawer.
if (document.querySelector('[data-ft3d-hud]')) {
    document.documentElement.classList.add('ft3d-hud-active');
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
    boot();
}
